import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, Receipt, Calendar } from 'lucide-react';
import Modal from '../components/Modal';
import './FormPage.css';
import { API_BASE_URL } from '../global';
import { showAlert, showConfirm } from '../components/AlertPopup';

export default function Expenses() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expenses, setExpenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({ id: null, descricao: '', valor: '', data_vencimento: '', status: 'Pendente' });

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/despesas.php`);
      const data = await response.json();
      setExpenses(data);
    } catch (error) {
      console.error('Erro ao buscar despesas:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const method = formData.id ? 'PUT' : 'POST';
    
    const payload = { ...formData };
    payload.valor = parseFloat(payload.valor.toString().replace(',', '.')) || 0;

    try {
      const response = await fetch(`${API_BASE_URL}/despesas.php`, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (result.success) {
        setIsModalOpen(false);
        fetchExpenses();
      } else {
        showAlert('Aviso','Erro ao salvar despesa');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const handleEdit = (expense) => {
    setFormData({ 
      id: expense.id, 
      descricao: expense.descricao, 
      valor: expense.valor, 
      data_vencimento: expense.data_vencimento, 
      status: expense.status 
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await showConfirm('Confirmação', 'Tem certeza que deseja remover esta despesa?');
    if (!confirmed) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/despesas.php?id=${id}`, {
        method: 'DELETE'
      });
      const result = await response.json();
      if (result.success) {
        fetchExpenses();
      } else {
        showAlert('Aviso','Erro ao excluir despesa');
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  };

  const openNewModal = () => {
    setFormData({ id: null, descricao: '', valor: '', data_vencimento: '', status: 'Pendente' });
    setIsModalOpen(true);
  };

  const filteredExpenses = expenses.filter(expense => 
    (expense.descricao && expense.descricao.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Despesas</h1>
          <p className="page-subtitle">Acompanhe as contas a pagar, pagamentos de fornecedores e demais despesas</p>
        </div>
        <button className="btn-primary" onClick={openNewModal} style={{ width: 'auto', padding: '0.75rem 1.25rem' }}>
          <Plus size={18} />
          Nova Despesa
        </button>
      </div>

      <div className="card list-card">
        <div className="card-header">
          <div className="search-bar" style={{ width: '100%', maxWidth: '400px' }}>
            <Search size={18} className="search-icon" />
            <input 
              type="text" 
              placeholder="Buscar despesa..." 
              className="search-input" 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="table-responsive">
          <table className="data-table">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map((expense) => (
                <tr key={expense.id}>
                  <td>
                    <div className="font-medium" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Receipt size={16} className="text-muted" /> {expense.descricao}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                      <Calendar size={14} /> {expense.data_vencimento}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: 'bold' }}>
                      R$ {Number(expense.valor).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${expense.status === 'Paga' ? 'badge-success' : 'badge-danger'}`}>
                      {expense.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn-icon" title="Editar Despesa" onClick={() => handleEdit(expense)}><Edit2 size={16} /></button>
                      <button className="btn-icon danger" title="Excluir Despesa" onClick={() => handleDelete(expense.id)}><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredExpenses.length === 0 && (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                    Nenhuma despesa encontrada.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        title={formData.id ? "Editar Despesa" : "Nova Despesa"}
      >
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="form-group flex-2">
              <label>Descrição da Despesa</label>
              <input type="text" name="descricao" value={formData.descricao} onChange={handleInputChange} placeholder="Ex: Conta de Luz" required className="standard-input" />
            </div>
            <div className="form-group flex-1">
              <label>Valor (R$)</label>
              <input type="number" step="0.01" name="valor" value={formData.valor} onChange={handleInputChange} placeholder="0.00" required className="standard-input" />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="form-group flex-1">
              <label>Data de Vencimento</label>
              <input type="date" name="data_vencimento" value={formData.data_vencimento} onChange={handleInputChange} required className="standard-input" />
            </div>
            <div className="form-group flex-1">
              <label>Status do Pagamento</label>
              <select name="status" value={formData.status} onChange={handleInputChange} className="standard-input" required>
                <option value="Pendente">Pendente</option>
                <option value="Paga">Paga</option>
              </select>
            </div>
          </div>
          <div className="form-actions" style={{ justifyContent: 'flex-end', gap: '0.75rem', marginTop: '0.5rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            <button type="button" className="btn-outline" onClick={() => setIsModalOpen(false)}>Cancelar</button>
            <button type="submit" className="btn-primary" style={{ width: 'auto', padding: '0.6rem 1.25rem' }}>Salvar Despesa</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
